import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
    Calendar as CalendarIcon, Map, User, Plus, Bell, LogOut,
    Sun, Moon, Shield, Home as HomeIcon, Share2, History, Sparkles, Crown,
    MessageCircle, CreditCard,
} from "lucide-react";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import PublicationQuotaBadge from "./PublicationQuotaBadge";

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const nav = useNavigate();
    const loc = useLocation();
    const [unreadChats, setUnreadChats] = useState(0);
    const [isFinanceAdmin, setIsFinanceAdmin] = useState(false);

    useEffect(() => {
        if (!user) { setUnreadChats(0); return; }
        let cancelled = false;
        const load = () => {
            api.get("/me/active-chats").then(r => {
                if (!cancelled) {
                    setUnreadChats((r.data || []).reduce((a, c) => a + (c.unread || 0), 0));
                }
            }).catch(() => { /* ignore */ });
        };
        load();
        const t = setInterval(load, 15000);
        return () => { cancelled = true; clearInterval(t); };
    }, [user, loc.pathname]);

    useEffect(() => {
        if (!user || user.role !== "admin") { setIsFinanceAdmin(false); return; }
        api.get("/me/finance-admin")
            .then(r => setIsFinanceAdmin(!!r.data?.finance_admin))
            .catch(() => setIsFinanceAdmin(false));
    }, [user]);

    const navItems = [
        { to: "/", label: "Accueil", icon: HomeIcon, testid: "nav-home" },
        { to: "/pov", label: "POV", icon: Sparkles, testid: "nav-pov" },
        ...(user ? [{ to: "/chat", label: "Tchat", icon: MessageCircle, testid: "nav-chat", badge: unreadChats }] : []),
        { to: "/agenda", label: "Agenda", icon: CalendarIcon, testid: "nav-agenda" },
        { to: "/share", label: "Partager", icon: Share2, testid: "nav-share" },
    ];
    return (
        <div className="min-h-screen flex flex-col bg-background">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
                    <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
                        <Logo size={36} />
                    </Link>
                    <nav className="hidden md:flex items-center gap-1">
                        {navItems.map(({ to, label, icon: Icon, testid, badge }) => (
                            <Link key={to} to={to} data-testid={testid}
                                className={`relative px-3 py-2 rounded-full text-[14px] font-semibold flex items-center gap-2 transition-all hover:bg-accent ${loc.pathname === to ? "bg-accent text-accent-foreground" : "text-foreground/70"}`}>
                                <Icon size={16} />{label}
                                {badge > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-discover-gradient text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                                        data-testid={`${testid}-badge`}>
                                        {badge}
                                    </span>
                                )}
                            </Link>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        {user && (
                            <Button onClick={() => nav("/publish")} size="sm"
                                className="rounded-full bg-discover-gradient text-white shadow-md hover:opacity-95 px-4"
                                data-testid="header-publish-btn">
                                <Plus size={16} className="mr-1" />Ajouter
                            </Button>
                        )}
                        <button onClick={toggle} aria-label="theme"
                            className="p-2 rounded-full hover:bg-accent" data-testid="theme-toggle">
                            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button data-testid="user-menu-trigger" className="flex items-center gap-2">
                                        <PublicationQuotaBadge />
                                        <Avatar className="h-9 w-9 ring-2 ring-primary/20">
                                            <AvatarFallback className="bg-discover-gradient text-white font-bold">
                                                {(user.prenom || "D")[0]?.toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                                    <DropdownMenuLabel className="font-bold">
                                        {user.prenom} {user.is_pro_tag && <span className="text-xs px-1.5 py-0.5 ml-1 rounded bg-emerald-100 text-emerald-700">PRO</span>}
                                        <div className="text-xs font-normal text-muted-foreground">{user.grade?.name}</div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => nav("/profile")} data-testid="menu-profile">
                                        <User size={14} className="mr-2" />Mon profil
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/agenda")} data-testid="menu-agenda">
                                        <CalendarIcon size={14} className="mr-2" />Agenda
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/map")} data-testid="menu-map">
                                        <Map size={14} className="mr-2" />Carte
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/notifications")} data-testid="menu-notif">
                                        <Bell size={14} className="mr-2" />Notifications
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/past-events")} data-testid="menu-past-events">
                                        <History size={14} className="mr-2" />Événements passés
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/pro")} data-testid="menu-pro">
                                        <Crown size={14} className="mr-2 text-amber-500" />Discoveur Pro
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => nav("/billing")} data-testid="menu-billing">
                                        <CreditCard size={14} className="mr-2" />Mes paiements & factures
                                    </DropdownMenuItem>
                                    {user.role === "admin" && (
                                        <DropdownMenuItem onClick={() => nav("/admin/share-digest")} data-testid="menu-admin-share">
                                            <Share2 size={14} className="mr-2 text-pink-500" />Partager les évènements
                                        </DropdownMenuItem>
                                    )}
                                    {user.role === "admin" && (user.email || "").toLowerCase() === "discoveuriste@gmail.com" && (
                                        <DropdownMenuItem onClick={() => nav("/admin/analytics")} data-testid="menu-admin-analytics">
                                            <Shield size={14} className="mr-2 text-sky-500" />Analytics
                                        </DropdownMenuItem>
                                    )}
                                    {user.role === "admin" && (
                                        <DropdownMenuItem onClick={() => nav("/admin/cvf")} data-testid="menu-admin-cvf">
                                            <Shield size={14} className="mr-2 text-fuchsia-500" />CVF · Flyers IA
                                        </DropdownMenuItem>
                                    )}
                                    {user.role === "admin" && (
                                        <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin">
                                            <Shield size={14} className="mr-2" />Admin
                                        </DropdownMenuItem>
                                    )}
                                    {isFinanceAdmin && (
                                        <DropdownMenuItem onClick={() => nav("/admin/payments")} data-testid="menu-admin-payments">
                                            <CreditCard size={14} className="mr-2 text-emerald-500" />Paiements HelloAsso
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="menu-logout">
                                        <LogOut size={14} className="mr-2" />Se déconnecter
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <>
                                <Button variant="ghost" size="sm" onClick={() => nav("/login")} className="rounded-full" data-testid="header-login-btn">Se connecter</Button>
                                <Button size="sm" onClick={() => nav("/register")} className="rounded-full bg-discover-gradient text-white" data-testid="header-register-btn">S'inscrire</Button>
                            </>
                        )}
                    </div>
                </div>
            </header>
            <main className="flex-1">{children}</main>
            {/* Mobile bottom nav — 6 icons on ONE line */}
            {user && (
                <nav className="md:hidden sticky bottom-0 z-30 backdrop-blur-xl bg-background/85 border-t border-border/60">
                    <div className="grid grid-cols-6 max-w-7xl mx-auto">
                        {[
                            { to: "/", label: "Accueil", icon: HomeIcon, t: "mob-home" },
                            { to: "/pov", label: "POV", icon: Sparkles, t: "mob-pov" },
                            { to: "/chat", label: "Tchat", icon: MessageCircle, t: "mob-chat", badge: unreadChats },
                            { to: "/publish", label: "Ajouter", icon: Plus, t: "mob-publish", primary: true },
                            { to: "/agenda", label: "Agenda", icon: CalendarIcon, t: "mob-agenda" },
                            { to: "/profile", label: "Profil", icon: User, t: "mob-profile" },
                        ].map(({ to, label, icon: Icon, t, primary, badge }) => (
                            <Link key={to} to={to} data-testid={t}
                                className={`relative flex flex-col items-center gap-0.5 py-1.5 text-[9px] leading-tight whitespace-nowrap truncate px-0.5 ${primary ? "text-white" : (loc.pathname === to ? "text-primary" : "text-foreground/60")}`}>
                                <span className={`flex items-center justify-center ${primary ? "rounded-full bg-discover-gradient w-9 h-9 -mt-2.5 shadow-lg" : ""}`}>
                                    <Icon size={primary ? 18 : 16} />
                                </span>
                                <span className="truncate max-w-full">{label}</span>
                                {badge > 0 && (
                                    <span className="absolute top-0 right-1 bg-discover-gradient text-white text-[8px] font-bold px-1 py-0.5 rounded-full"
                                        data-testid={`${t}-badge`}>
                                        {badge}
                                    </span>
                                )}
                            </Link>
                        ))}
                    </div>
                </nav>
            )}
            <Footer />
        </div>
    );
}

function Footer() {
    return (
        <footer className="mt-auto bg-muted/30 border-t border-border/60 py-10 text-[13px]" data-testid="footer">
            {/* iter53 — Manifesto band. Sits ABOVE the columns so it lands
                right between the page content (e.g. the last event tile
                on Home) and the Discover / Compte / Legal links. Doubles
                as SEO-rich content: all territories + intent keywords
                (events, culture, tourism…) enumerated in visible prose. */}
            <div className="max-w-4xl mx-auto px-4 md:px-8 mb-10 space-y-3 text-center"
                 data-testid="footer-manifesto">
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    Avec <strong className="text-foreground">Discoveur</strong>, trouvez
                    facilement les événements, concerts, festivals, spectacles, marchés,
                    sorties en famille, activités pour enfants, manifestations sportives,
                    expositions, soirées, loisirs, restaurants et bons plans près de chez
                    vous.
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    <span aria-hidden="true">🌍 </span>
                    Discoveur rassemble les habitants, visiteurs, associations,
                    organisateurs et professionnels de tous les territoires d'Outre-mer :{" "}
                    <strong>Martinique</strong>, <strong>Guadeloupe</strong>,{" "}
                    <strong>Guyane</strong>, <strong>La Réunion</strong>,{" "}
                    <strong>Mayotte</strong>, <strong>Saint-Martin</strong>,{" "}
                    <strong>Saint-Barthélemy</strong>,{" "}
                    <strong>Polynésie française</strong>,{" "}
                    <strong>Nouvelle-Calédonie</strong>, <strong>Wallis-et-Futuna</strong>{" "}
                    et <strong>Saint-Pierre-et-Miquelon</strong>.
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    <span aria-hidden="true">✨ </span>
                    Ensemble, faisons rayonner les événements, la culture, les loisirs,
                    le tourisme et les talents des Outre-mer.
                </p>
                <p className="text-sm md:text-base font-black bg-clip-text text-transparent bg-discover-gradient">
                    Discoveur : découvrez, partagez, participez !
                </p>
            </div>
            <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="col-span-2 md:col-span-1">
                    <Logo size={28} />
                    <p className="mt-3 text-muted-foreground">
                        Discoveur – La plateforme collaborative des sorties, activités et
                        événements des Outre-mer.
                    </p>
                </div>
                <div>
                    <div className="font-bold mb-2">Découvrir</div>
                    <Link to="/" className="block text-muted-foreground hover:text-foreground">Accueil</Link>
                    <Link to="/qui-sommes-nous" className="block text-muted-foreground hover:text-foreground">Qui sommes-nous</Link>
                    <Link to="/pov" className="block text-muted-foreground hover:text-foreground">POV</Link>
                    <Link to="/map" className="block text-muted-foreground hover:text-foreground">Carte</Link>
                    <Link to="/share" className="block text-muted-foreground hover:text-foreground">Partager</Link>
                    <Link to="/pro" className="block text-muted-foreground hover:text-foreground font-semibold">Discoveur Pro</Link>
                </div>
                <div>
                    <div className="font-bold mb-2">Compte</div>
                    <Link to="/profile" className="block text-muted-foreground hover:text-foreground">Profil</Link>
                    <Link to="/agenda" className="block text-muted-foreground hover:text-foreground">Agenda</Link>
                    <Link to="/don" className="block text-muted-foreground hover:text-foreground" data-testid="footer-don-link">
                        <span className="text-pink-500 font-semibold">♥</span> Faire un don
                    </Link>
                </div>
                <div>
                    <div className="font-bold mb-2">Legal</div>
                    <Link to="/charter" className="block text-muted-foreground hover:text-foreground">Charte d'utilisation</Link>
                    <Link to="/contact" className="block text-muted-foreground hover:text-foreground">Contact / Réclamation</Link>
                    <span className="block text-muted-foreground text-xs mt-2">Données protégées, RGPD-conforme.</span>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8 pt-6 border-t border-border/50 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>© {new Date().getFullYear()} Discoveur - Tous droits réservés.</span>
                <span className="select-none">Contenu protege - reproduction interdite.</span>
            </div>
        </footer>
    );
}
